<template>
  <div class="pvplog-wrapper">
    <div v-if="loading" class="text-center py-5">
      <b-spinner label="Loading..." variant="primary"></b-spinner>
      <p class="text-muted mt-2">Loading PvP logs...</p>
    </div>

    <div v-else class="animated fadeIn">

      <!-- PvP Grid -->
      <div class="rounded shadow-sm border overflow-hidden" style="height: 700px;">
        <ag-grid-vue style='width: 100%; height: 100%;' class='ag-theme-alpine-dark pvplog-grid'
                     :gridOptions='gridOptions'
                     :rowData="pvplogs && pvplogs.data ? pvplogs.data : []"
                     :masterDetail="true"
                     :detailCellRenderer="detailCellRenderer"
                     @first-data-rendered="onFirstDataRendered">
        </ag-grid-vue>
      </div>
    </div>

      </div>
    </div>

  </div>
</template>

<script>
import {AgGridVue} from 'ag-grid-vue'
import 'ag-grid-enterprise'
import PvPDetailCellRenderer from './PvPDetailCellRenderer.js'
import Servers from '../../../_servers'
import moment from 'moment'
import {mapGetters} from 'vuex'
import iziToast from 'izitoast'
import {DefaultGridOptions} from "@/default_table_settings";
import {applyGridPersistence} from "@/grid_persistence"

const PvP = 1
const Wild = 1 << 1
const Trainer = 1 << 2
const Ranked = 1 << 3
const Random = 1 << 4
const Friendly = 1 << 5

export default {
  props: {
    username: { type: String, required: true },
    server: { type: String, required: true }
  },
  data () {
    return {
      gridOptions: null,
      showToolPanel: false,
      modalTitle: '',
      loading: false,
      servers: Servers,
      detailCellRenderer: 'PvPDetailCellRenderer'
    }
  },
  computed: {
    ...mapGetters({
      pvplogs: 'PVPlogs'
    }),
    rankedMatches() {
        if (!this.pvplogs || !this.pvplogs.data) return [];
        return this.pvplogs.data.filter(m => m.type & Ranked);
    },
    rankedWins() {
        return this.rankedMatches.filter(m => m.won && !m.isDraw).length;
    },
    rankedLosses() {
        return this.rankedMatches.filter(m => !m.won && !m.isDraw).length;
    },
    rankedWinRate() {
        const played = this.rankedMatches.filter(m => !m.isDraw);
        if (played.length === 0) return '0.0';
        return ((this.rankedWins / played.length) * 100).toFixed(1);
    }
  },
  components: {
    'ag-grid-vue': AgGridVue,
    PvPDetailCellRenderer
  },
  watch: {
    username: 'fetchData',
    server: 'fetchData',
    pvplogs() {
      this.loading = false
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      if (this.username && this.server) {
        this.loading = true;
        this.emit('searchPVPlog', {
          server: this.server,
          username: this.username
        })
      }
    },
    showShared(type, value) {
      this.$store.commit('setSharedIPs', null);
      let event = '';
      let payload = { server: this.server };
      let title = '';

      if (type === 'IP') {
          title = `Accounts sharing IP: ${value}`;
          event = 'getSharedIPs';
          payload.ip = value;
      } else if (type === 'MAC') {
          title = `Accounts sharing MAC: ${value}`;
          event = 'getSharedMACs';
          payload.mac = value;
      }

      if (event) {
          this.emit(event, payload);
          this.$root.$emit('show-shared-modal', {
              title: title,
              server: this.server
          });
      }
    },
    onCellClicked(params) {
        if (params.event.target.classList.contains('clickable-net')) {
            const type = params.event.target.getAttribute('data-type');
            const val = params.event.target.getAttribute('data-val');
            if (type && val) {
                this.showShared(type, val);
            }
        }
    },
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    formatMinutes (minutes) {
      let h = Math.floor(minutes / 60)
      let m = minutes % 60
      return h <= 0 ? m + 'm' : h + 'h';
    },
    formatPrice (value) {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    formatDateDisplay(date) {
        if (!date || date.startsWith('0001')) return '-';
        return moment(date).format('YYYY-MM-DD HH:mm');
    },
    getDateTooltip(date) {
        if (!date || date.startsWith('0001')) return '';
        return moment(date).fromNow();
    },
    getMatchTooltip(m) {
        const res = m.won ? 'WIN' : 'LOSS';
        const time = m.startTime ? moment(m.startTime).fromNow() : 'unknown time';
        return `${res} vs ${m.opponent.name} (${time})`;
    },
    getWinRateVariant(op) {
        const wr = (op.wins / op.count) * 100;
        if (wr > 80 && op.count > 3) return 'danger';
        if (wr > 60) return 'warning';
        return 'dark';
    },
    getWinRateColor(op) {
        const wr = (op.wins / op.count) * 100;
        if (wr > 80 && op.count > 3) return 'text-danger';
        if (wr > 60) return 'text-warning';
        return 'text-success';
    },
    createColumnDefs () {
      return [
        {
          headerName: '',
          width: 30,
          pinned: 'left',
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: {
              suppressCount: true,
              innerRenderer: () => ''
          },
          resizable: true, sortable: false, filter: false, suppressMenu: true
        },
        {
          headerName: 'Result',
          width: 85,
          pinned: 'left',
          filter: 'agSetColumnFilter',
          valueGetter: (params) => {
              if (params.data && params.data.isDraw) return 'DRAW';
              return params.data && params.data.won ? 'WIN' : 'LOSS';
          },
          cellRenderer: (params) => {
              const text = params.value || 'LOSS';
              const color = text === 'WIN' ? 'success' : (text === 'DRAW' ? 'secondary' : 'danger');
              return `<span class="badge badge-${color} shadow-sm" style="min-width: 45px; font-weight: 800; font-size: 0.7rem">${text}</span>`;
          }
        },
        {
          headerName: 'Opponent',
          field: 'opponent.name',
          width: 120,
          pinned: 'left',
          filter: 'agTextColumnFilter',
          cellRenderer: (params) => {
             const target = this.$router.resolve({
               name: 'User',
               params: { username: params.value },
               query: { server: this.server }
             }).href;
             return `<a href="${target}" class="font-weight-bold text-primary">${params.value}</a>`;
          }
        },
        {
          headerName: 'Battle',
          headerClass: 'text-center border-left',
          children: [
              {
                  headerName: 'Type',
                  field: 'type',
                  width: 100,
                  filter: 'agSetColumnFilter',
                  valueGetter: (params) => {
                      let types = []
                      if(params.data.type & PvP) types.push("PvP")
                      if(params.data.type & Ranked) types.push("Ranked")
                      if(params.data.type & Random) types.push("Random")
                      if(params.data.type & Friendly) types.push("Friendly")
                      return types.join('/')
                  },
                  cellRenderer: params => `<span class="text-info" style="font-size: 0.75rem">${params.value}</span>`
              },
              {
                  headerName: 'Duration',
                  headerComponentParams: { template: '<div><i class="fa fa-clock-o mr-1"></i> Duration</div>' },
                  width: 90,
                  sortable: true,
                  comparator: (a, b) => a - b,
                  valueGetter: (params) => {
                      if(!params.data.startTime || !params.data.endTime) return 0
                      return moment(params.data.endTime).diff(moment(params.data.startTime));
                  },
                  cellRenderer: params => params.value ? moment.utc(params.value).format('mm:ss') : '-',
                  cellStyle: { 'font-family': 'monospace', 'font-size': '0.75rem' }
              }
          ]
        },
        {
          headerName: 'Teams',
          headerClass: 'text-center border-left',
          children: [
              {
                  headerName: "User Team",
                  width: 220,
                  filter: 'agTextColumnFilter',
                  valueGetter: (params) => {
                      if (!params.data.user || !params.data.user.pokemon) return '';
                      return Object.values(params.data.user.pokemon).map(p => p.name).join(', ');
                  },
                  cellRenderer: (params) => {
                      if (!params.data.user || !params.data.user.pokemon) return '';
                      return `<div class="d-flex align-items-center" style="gap: 2px">` +
                        Object.values(params.data.user.pokemon).slice(0, 6).map(p => {
                          return `<span class="badge badge-dark border border-secondary" style="font-size: 0.6rem; padding: 2px 4px; opacity: 0.8" title="Lvl ${p.level}">${p.name}</span>`;
                        }).join('') + `</div>`
                  }
              },
              {
                  headerName: "Opponent Team",
                  width: 220,
                  filter: 'agTextColumnFilter',
                  valueGetter: (params) => {
                      if (!params.data.opponent || !params.data.opponent.pokemon) return '';
                      return Object.values(params.data.opponent.pokemon).map(p => p.name).join(', ');
                  },
                  cellRenderer: (params) => {
                      if (!params.data.opponent || !params.data.opponent.pokemon) return '';
                      return `<div class="d-flex align-items-center" style="gap: 2px">` +
                        Object.values(params.data.opponent.pokemon).slice(0, 6).map(p => {
                          return `<span class="badge badge-dark border border-secondary" style="font-size: 0.6rem; padding: 2px 4px; opacity: 0.8" title="Lvl ${p.level}">${p.name}</span>`;
                        }).join('') + `</div>`
                  }
              }
          ]
        },
        {
          headerName: 'Technical',
          headerClass: 'text-center border-left',
          children: [
              {
                  headerName: "+/-",
                  field: 'user.ratingChange',
                  width: 55,
                  sortable: true,
                  filter: 'agNumberColumnFilter',
                  cellRenderer: params => {
                      const val = params.value || 0;
                      const color = val > 0 ? 'text-success' : (val < 0 ? 'text-danger' : 'text-muted');
                      return `<span class="${color} font-weight-bold" style="font-size: 0.75rem">${val > 0 ? '+' : ''}${val}</span>`
                  }
              },
              {
                  headerName: "Network (User vs Opponent)",
                  width: 240,
                  filter: 'agTextColumnFilter',
                  valueGetter: params => {
                      const u = params.data.user || {};
                      const o = params.data.opponent || {};
                      return `${u.IP || ''} ${u.MAC || ''} ${o.IP || ''} ${o.MAC || ''}`;
                  },
                  cellRenderer: params => {
                      const u = params.data.user || {};
                      const o = params.data.opponent || {};
                      return `<div class="d-flex w-100 justify-content-between align-items-center" style="height: 100%">
                          <div class="mr-2 border-right border-secondary pr-2" style="line-height: 1.1">
                              <small class="d-block text-info clickable-net" data-type="IP" data-val="${u.IP || ''}" style="font-size: 0.65rem">${u.IP || '-'}</small>
                              <small class="text-muted clickable-net" data-type="MAC" data-val="${u.MAC || ''}" style="font-size: 0.6rem">${u.MAC || '-'}</small>
                          </div>
                          <div style="line-height: 1.1">
                              <small class="d-block text-danger clickable-net" data-type="IP" data-val="${o.IP || ''}" style="font-size: 0.65rem">${o.IP || '-'}</small>
                              <small class="text-muted clickable-net" data-type="MAC" data-val="${o.MAC || ''}" style="font-size: 0.6rem">${o.MAC || '-'}</small>
                          </div>
                      </div>`
                  }
              }
          ]
        },
        {
          field: 'startTime',
          headerName: 'Date',
          width: 130,
          sortable: true,
          sort: 'desc',
          filter: 'agDateColumnFilter',
          cellRenderer: params => `<span class="text-muted" style="font-size: 0.7rem">${moment(params.value).format('YYYY-MM-DD HH:mm')}</span>`
        }
      ]
    },
    getContextMenuItems(params) {
      let result = ['copy', 'copyWithHeaders', 'separator', 'export'];
      const isDetail = params.node && params.node.detail;
      let data = params.node && params.node.data ? params.node.data : null;
      if (!data && isDetail && params.node.parent) {
          data = params.node.parent.data;
      }

      if (data) {
        if (isDetail) {
          // Replace standard copy with summary copy for detail rows
          result = [
            {
              name: 'Copy Match Summary',
              icon: '<i class="fa fa-copy"></i>',
              action: () => {
                this.copyMatchSummaryToClipboard(data);
              }
            },
            'separator',
            'export'
          ];
        }

        result.push('separator');
        result.push({
          name: 'Copy Replay Command',
          icon: '<i class="fa fa-clipboard"></i>',
          action: () => {
            const id = data.ID;
            const username = this.username;
            const command = `/replay ${id}, ${username}`;
            navigator.clipboard.writeText(command);
            iziToast.success({
                title: 'Copied',
                message: 'Replay command copied to clipboard',
                position: 'topRight'
            });
          }
        });
      }
      return result;
    },
    copyMatchSummaryToClipboard(data) {
      if (!data || !data.user || !data.opponent) return;
      
      const calculateHP = (poke) => {
        const hp_after = poke.hp_after || 0;
        const hp_base = poke.hp_base || 0;
        const hp_iv = poke.hp_iv || 0;
        const hp_ev = poke.hp_ev || 0;
        const level = poke.level || 0;
        if (!hp_base || !level) return 0;
        const maxHP = Math.floor(0.01 * (2 * hp_base + hp_iv + Math.floor(0.25 * hp_ev)) * level) + level + 10;
        if (maxHP <= 0) return 0;
        return Math.min(100, Math.max(0, Math.round((hp_after / maxHP) * 100)));
      };

      const formatTeam = (player) => {
        if (!player.pokemon) return "No team info";
        return Object.values(player.pokemon).map(p => {
          const hp = calculateHP(p);
          return `- ${p.name} (Lvl ${p.level})${p.heldItem ? ' @ ' + p.heldItem : ''} [HP: ${hp}%]`;
        }).join('\n');
      };

      const result = data.isDraw ? 'DRAW' : (data.won ? 'WIN' : 'LOSS');
      const summary = `Match Summary: ${data.user.name} vs ${data.opponent.name}\n` +
        `Result: ${result}\n\n` +
        `${data.user.name}'s Team:\n${formatTeam(data.user)}\n\n` +
        `${data.opponent.name}'s Team:\n${formatTeam(data.opponent)}`;

      navigator.clipboard.writeText(summary);
      iziToast.success({
        title: 'Copied',
        message: 'Match summary copied to clipboard',
        position: 'topRight'
      });
    },
  },
  beforeMount () {
    this.gridOptions = Object.assign({}, DefaultGridOptions, {
      animateRows: true,
      headerHeight: 40,
      groupHeaderHeight: 30,
      floatingFiltersHeight: 40,
      rowSelection: 'multiple',
      suppressRowClickSelection: true,
      masterDetail: true,
      context: { componentParent: this },
      onCellClicked: this.onCellClicked,
      getContextMenuItems: this.getContextMenuItems,
      getRowHeight: (params) => {
        if (params.node && params.node.detail) {
          const userPokes = params.data.user.pokemon ? Object.keys(params.data.user.pokemon).length : 0;
          const oppPokes = params.data.opponent.pokemon ? Object.keys(params.data.opponent.pokemon).length : 0;
          return (Math.max(userPokes, oppPokes) * 95) + 50;
        }
        return 50;
      }
    })
    applyGridPersistence(this.gridOptions, 'pvp-log-grid')
    this.gridOptions.columnDefs = this.createColumnDefs()
  }
}
</script>

<style scoped>
.pvplog-wrapper { min-height: 400px; }
.bg-dark-subtle { background-color: rgba(0, 0, 0, 0.15) !important; }
.bg-danger-subtle { background-color: rgba(248, 108, 107, 0.1) !important; }
.bg-warning-subtle { background-color: rgba(255, 193, 7, 0.1) !important; }
.hover-highlight:hover { background-color: rgba(255, 255, 255, 0.05) !important; border-color: rgba(255, 255, 255, 0.2) !important; }
.transition-all { transition: all 0.2s ease-in-out; }
.result-dot { width: 12px; height: 12px; border-radius: 50%; border: 1px solid rgba(255,255,255,0.1); }
.gap-1 { gap: 0.25rem; }
.gap-2 { gap: 0.5rem; }
/deep/ .pvplog-grid .ag-header-group-cell {
    font-weight: bold; font-size: 0.75rem; text-transform: uppercase; color: #8a9196; background-color: rgba(0,0,0,0.1);
}
/deep/ .pvplog-grid .ag-cell { display: flex; align-items: center; line-height: 1.2 !important; }
/deep/ .pvplog-grid .ag-row-hover { background-color: rgba(32, 168, 216, 0.05) !important; }
.clickable-net { cursor: pointer; transition: all 0.2s; }
.clickable-net:hover { color: #fff !important; text-decoration: underline; filter: brightness(1.2); }
.opacity-75 { opacity: 0.75; }

/deep/ .custom-ag-menu-item { color: #babfbf; transition: background 0.1s; font-size: 0.8rem; }
/deep/ .custom-ag-menu-item:hover { background-color: #222628; color: #fff; cursor: pointer; }
</style>
